import { useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSpinner, FaSync, FaPlus, FaTrash } from 'react-icons/fa';
import { SessionSelectorProps, SessionSelectorHandle } from './types';
import { useSessionSelector } from './hooks';
import { formatDate } from './utils';
import { SessionCard } from './SessionCard';

/**
 * SessionSelector Component
 * Displays and manages training sessions with CRUD operations
 */
const SessionSelector = forwardRef<SessionSelectorHandle, SessionSelectorProps>(({
  currentSessionId,
  onSessionSelect,
  onNewSession,
  onSessionDelete,
  onReset
}, ref) => {
  const { t } = useTranslation('training');

  const {
    sessions,
    loading,
    error,
    deletingSession,
    editingSession,
    editingName,
    savingName,
    loadSessions,
    handleEditSessionName,
    handleSaveSessionName,
    handleCancelEdit,
    handleDeleteSession,
    setEditingName
  } = useSessionSelector();

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Expose refreshSessions function to parent component
  useImperativeHandle(ref, () => ({
    refreshSessions: loadSessions
  }), [loadSessions]);

  const handleDelete = useCallback((sessionId: string) => {
    handleDeleteSession(sessionId, onSessionDelete);
  }, [handleDeleteSession, onSessionDelete]);

  if (loading) {
    return (
      <div className="bg-card rounded-xl shadow-sm border border-border flex flex-col h-full">
        <div className="flex flex-col items-center justify-center flex-1 py-16">
          <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center mb-3">
            <FaSpinner className="animate-spin text-primary" />
          </div>
          <span className="text-sm text-muted-foreground">{t('sessionSelector.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-xl shadow-sm border border-border flex flex-col h-full">
        <div className="flex flex-col items-center justify-center flex-1 py-12 px-6">
          <div className="bg-red-50 border border-red-200/80 rounded-lg p-5 w-full max-w-sm">
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-red-500 text-xs font-bold">!</span>
              </div>
              <div>
                <p className="text-sm text-red-700 leading-relaxed">{error}</p>
                <button
                  type="button"
                  onClick={loadSessions}
                  className="mt-3 text-xs font-medium text-red-600 hover:text-red-800 underline underline-offset-2"
                >
                  {t('sessionSelector.tryAgain')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-foreground tracking-tight">
              {t('sessionSelector.title')}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t('sessionSelector.sessionCount', '{{count}} session(s)', { count: sessions.length })}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={loadSessions}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-accent transition-all duration-150"
              title={t('sessionSelector.refresh')}
            >
              <FaSync size={11} />
            </button>
            <button
              type="button"
              onClick={onNewSession}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-emerald-600 hover:bg-emerald-50 transition-all duration-150"
              title={t('sessionSelector.newSession')}
            >
              <FaPlus size={11} />
            </button>
            {onReset && (
              <button
                type="button"
                onClick={onReset}
                disabled={sessions.length === 0}
                className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-150 ${
                  sessions.length === 0
                    ? 'text-muted-foreground/50 cursor-not-allowed'
                    : 'text-muted-foreground hover:text-red-600 hover:bg-red-50'
                }`}
                title={t('sessionSelector.deleteAllData')}
              >
                <FaTrash size={11} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
        {sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
              <svg className="w-5 h-5 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-2.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <p className="text-sm">{t('sessionSelector.noSessions')}</p>
          </div>
        ) : (
          <div role="list" className="space-y-1.5">
            {sessions.filter(session => session && session.sessionId).map((session) => (
              <div role="listitem" key={session.sessionId}>
                <SessionCard
                  session={session}
                  isCurrent={currentSessionId === session.sessionId}
                  isDeleting={deletingSession === session.sessionId}
                  isEditing={editingSession === session.sessionId}
                  isSaving={savingName === session.sessionId}
                  editingName={editingName}
                  onSelect={() => onSessionSelect(session)}
                  onDelete={() => handleDelete(session.sessionId)}
                  onEditName={() => handleEditSessionName(session.sessionId, session.sessionName || '')}
                  onSaveName={() => handleSaveSessionName(session.sessionId)}
                  onCancelEdit={handleCancelEdit}
                  onEditingNameChange={setEditingName}
                  formatDate={formatDate}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

SessionSelector.displayName = 'SessionSelector';

export default SessionSelector;
